import {
  CashSessionSummary,
  CashSessionSummaryDocumentTypeAmount,
  CashSessionSummaryGroup,
} from '@/@types/cash-management';
import { COMPANY_NAME } from '@/constants';
import { CSSProperties, Ref } from 'react';
import {
  cashSessionSummaryStatusLabel,
  formatSummaryCurrency,
  formatSummaryDateTime,
} from './CashSessionSummaryPrint.utils';

type CashSessionSummaryPdfPrintProps = {
  summary?: CashSessionSummary;
  containerRef: Ref<HTMLDivElement>;
  emittedBy?: string;
};

type SectionProps = {
  title: string;
  groups: CashSessionSummaryGroup[];
};

type SummaryListProps = {
  title: string;
  items: CashSessionSummaryDocumentTypeAmount[];
  totalLabel: string;
};

const tableStyle: CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: '10px',
};

const cellStyle: CSSProperties = {
  padding: '3px 4px',
};

const totalCellStyle: CSSProperties = {
  ...cellStyle,
  fontWeight: 700,
};

const summaryHeaderStyle: CSSProperties = {
  borderBottom: '1px solid #4A5568',
  fontSize: 12,
  margin: '0 0 8px',
  paddingBottom: 2,
};

function sumSummaryItems(items: CashSessionSummaryDocumentTypeAmount[]) {
  return items.reduce((total, item) => total + item.amount, 0);
}

function SummarySection({ title, groups }: SectionProps) {
  return (
    <section style={{ marginTop: 18 }}>
      <h2 style={{ fontSize: 13, margin: '0 0 8px' }}>{title}</h2>
      {groups.length ? (
        groups.map((group) => (
          <div key={group.documentType.id} style={{ marginBottom: 12 }}>
            <h3 style={{ fontSize: 11, margin: '0 0 4px' }}>
              {group.documentType.number} - {group.documentType.name}
            </h3>
            <table style={tableStyle}>
              <tbody>
                {group.items.map((item) => (
                  <tr key={item.id}>
                    <td style={{ ...cellStyle, width: 110 }}>
                      {formatSummaryDateTime(item.createdAt)}
                    </td>
                    <td style={cellStyle}>{item.description}</td>
                    <td style={{ ...cellStyle, textAlign: 'right', width: 90 }}>
                      {formatSummaryCurrency(item.amount)}
                    </td>
                  </tr>
                ))}
                <tr>
                  <td style={{ ...cellStyle, width: 110 }} />
                  <td style={{ ...cellStyle, textAlign: 'right' }}>
                    <strong>Subtotal</strong>
                  </td>
                  <td style={{ ...cellStyle, textAlign: 'right', width: 90 }}>
                    <strong>{formatSummaryCurrency(group.subtotal)}</strong>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        ))
      ) : (
        <p style={{ fontSize: 12, margin: 0 }}>Nenhum.</p>
      )}
    </section>
  );
}

function SummaryList({ title, items, totalLabel }: SummaryListProps) {
  const total = sumSummaryItems(items);

  return (
    <section>
      <h2 style={summaryHeaderStyle}>{title}</h2>
      {items.length ? (
        <table style={tableStyle}>
          <tbody>
            {items.map((item) => (
              <tr key={item.documentType.id}>
                <td style={cellStyle}>
                  {item.documentType.number} - {item.documentType.name}
                </td>
                <td style={{ ...cellStyle, textAlign: 'right' }}>
                  {formatSummaryCurrency(item.amount)}
                </td>
              </tr>
            ))}
            <tr>
              <td style={totalCellStyle}>{totalLabel}</td>
              <td style={{ ...totalCellStyle, textAlign: 'right' }}>
                {formatSummaryCurrency(total)}
              </td>
            </tr>
          </tbody>
        </table>
      ) : (
        <p style={{ fontSize: 12, margin: 0 }}>Sem valores.</p>
      )}
    </section>
  );
}

export function CashSessionSummaryPdfPrint({
  summary,
  containerRef,
  emittedBy,
}: CashSessionSummaryPdfPrintProps) {
  if (!summary) return <div ref={containerRef} />;

  const { cashSession, summaries } = summary;
  const emittedAt = formatSummaryDateTime(new Date().toISOString());

  return (
    <div
      ref={containerRef}
      style={{
        color: '#111111',
        fontFamily: 'Arial, sans-serif',
        fontSize: '10px',
        padding: '28px',
        width: '210mm',
      }}
    >
      <header
        style={{
          borderBottom: '1px solid #4A5568',
          display: 'flex',
          justifyContent: 'space-between',
          gap: 24,
          paddingBottom: 12,
        }}
      >
        <div>
          <h1 style={{ fontSize: 18, margin: 0 }}>Movimento de Caixa</h1>
          <p style={{ margin: '2px 0 0' }}>Emitido em: {emittedAt}</p>
          <p style={{ margin: '2px 0 0' }}>Emitido por: {emittedBy || '-'}</p>
        </div>
        <div style={{ minWidth: 220, textAlign: 'left' }}>
          <p style={{ margin: 0 }}>Empresa: {COMPANY_NAME}</p>
          <p style={{ margin: '2px 0 0' }}>Número: {cashSession.number}</p>
          <p style={{ margin: '2px 0 0' }}>Usuário: {cashSession.user.name}</p>
          <p style={{ margin: '2px 0 0' }}>
            Status: {cashSessionSummaryStatusLabel[cashSession.status]}
          </p>
          <p style={{ margin: '2px 0 0' }}>
            Data Abertura: {formatSummaryDateTime(cashSession.openedAt)}
          </p>
          {cashSession.closedAt ? (
            <p style={{ margin: '2px 0 0' }}>
              Data Fechamento: {formatSummaryDateTime(cashSession.closedAt)}
            </p>
          ) : null}
        </div>
      </header>

      <SummarySection title="Créditos" groups={summary.groups.credits} />
      <SummarySection title="Débitos" groups={summary.groups.debits} />

      <div
        style={{
          display: 'grid',
          gap: 16,
          gridTemplateColumns: '1fr 1fr',
          marginTop: 18,
        }}
      >
        <SummaryList
          title="Resumo de Créditos"
          items={summaries.credits}
          totalLabel="TOTAL GERAL"
        />
        <SummaryList
          title="Resumo de Débitos"
          items={summaries.debits}
          totalLabel="TOTAL GERAL"
        />
        <SummaryList
          title="Vendas por Tipo de Documento"
          items={summaries.salesByDocumentType}
          totalLabel="TOTAL VENDAS"
        />
        <section>
          <h2 style={summaryHeaderStyle}>Totais do Caixa</h2>
          <table style={tableStyle}>
            <tbody>
              <tr>
                <td style={cellStyle}>Saldo Inicial</td>
                <td style={{ ...cellStyle, textAlign: 'right' }}>
                  {formatSummaryCurrency(summaries.cashTotals.startingCash)}
                </td>
              </tr>
              <tr>
                <td style={cellStyle}>Total Créditos</td>
                <td style={{ ...cellStyle, textAlign: 'right' }}>
                  {formatSummaryCurrency(summaries.cashTotals.totalCredits)}
                </td>
              </tr>
              <tr>
                <td style={cellStyle}>Total Débitos</td>
                <td style={{ ...cellStyle, textAlign: 'right' }}>
                  {formatSummaryCurrency(summaries.cashTotals.totalDebits)}
                </td>
              </tr>
              <tr>
                <td style={cellStyle}>
                  <strong>Saldo Final</strong>
                </td>
                <td style={{ ...cellStyle, textAlign: 'right' }}>
                  <strong>
                    {formatSummaryCurrency(summaries.cashTotals.finalBalance)}
                  </strong>
                </td>
              </tr>
            </tbody>
          </table>
        </section>
      </div>
    </div>
  );
}
